// ═══════════════════════════════════════════════════════════════════
//  THE CANCELLATIONS WINDOW, IN SITE TIME
//
//  The two pickers are `datetime-local` inputs, which carry a wall-clock
//  reading with no zone ("2026-09-01T08:00"). The office reads them as
//  Pacific, whatever zone the browser happens to be in, so they are converted
//  here — explicitly, via siteTime.ts — to UTC instants for the API, and the
//  API's instants are shown back in Pacific.
// ═══════════════════════════════════════════════════════════════════

import {
  SITE_TIME_ZONE,
  addDays,
  todayInZone,
  zonedDateAndMinutes,
  zonedTimeToInstant,
} from "@/app/booking-calendar/lib/siteTime"

/** A `datetime-local` value: "YYYY-MM-DDTHH:mm" (seconds optional), no zone. */
export type LocalDateTime = string

function pad(value: number): string {
  return value.toString().padStart(2, "0")
}

/**
 * The instant at which the clock in `timeZone` reads `value`, as a UTC ISO string — or undefined when
 * `value` is empty or not a `datetime-local` reading. A time inside the spring-forward gap resolves to
 * the instant the clock jumps to; an ambiguous fall-back time resolves to one of its two instants.
 */
export function localToUtcIso(value: LocalDateTime, timeZone: string = SITE_TIME_ZONE): string | undefined {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value.trim())
  if (!match) return undefined
  const [, date, hour, minute, second] = match
  const hours = Number(hour)
  const minutes = Number(minute)
  if (hours > 23 || minutes > 59) return undefined
  const instant = zonedTimeToInstant(date!, (hours * 60) + minutes, timeZone)
  if (Number.isNaN(instant.getTime())) return undefined
  return new Date(instant.getTime() + (Number(second ?? 0) * 1000)).toISOString()
}

/** An instant as the `datetime-local` value that shows it on `timeZone`'s clock (to the minute). */
export function utcToLocal(instant: Date | string, timeZone: string = SITE_TIME_ZONE): LocalDateTime {
  const { date, minutes } = zonedDateAndMinutes(instant, timeZone)
  return `${date}T${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`
}

/** The default window: the last `days` whole site-local days, through the end of today. */
export function defaultWindow(days = 7, now: Date = new Date(), timeZone: string = SITE_TIME_ZONE): { start: LocalDateTime; end: LocalDateTime } {
  const today = todayInZone(timeZone, now)
  return {
    start: `${addDays(today, -(days - 1))}T00:00`,
    end: `${addDays(today, 1)}T00:00`,
  }
}

export type WindowValidation =
  | { ok: true; start: string; end: string }
  | { ok: false; error: string }

/** Both ends converted to UTC, and the window checked to end after it starts. */
export function validateWindow(start: LocalDateTime, end: LocalDateTime, timeZone: string = SITE_TIME_ZONE): WindowValidation {
  const startIso = localToUtcIso(start, timeZone)
  const endIso = localToUtcIso(end, timeZone)
  if (!startIso) return { ok: false, error: "Choose when the window starts." }
  if (!endIso) return { ok: false, error: "Choose when the window ends." }
  if (new Date(startIso).getTime() >= new Date(endIso).getTime()) {
    return { ok: false, error: "The window must end after it starts." }
  }
  return { ok: true, start: startIso, end: endIso }
}

const displayFormatters = new Map<string, Intl.DateTimeFormat>()

function cachedFormatter(key: string, create: () => Intl.DateTimeFormat): Intl.DateTimeFormat {
  let formatter = displayFormatters.get(key)
  if (!formatter) {
    formatter = create()
    displayFormatters.set(key, formatter)
  }
  return formatter
}

/** "Sep 1, 2026, 8:00 AM PDT" — an API instant on the site's clock, or "—" when there is none. */
export function formatInZone(instant: string | null | undefined, timeZone: string = SITE_TIME_ZONE): string {
  if (!instant) return "—"
  const value = new Date(instant)
  if (Number.isNaN(value.getTime())) return "—"
  // dateStyle/timeStyle cannot be combined with timeZoneName, so the zone's abbreviation is read separately.
  const text = cachedFormatter(`text:${timeZone}`, () =>
    new Intl.DateTimeFormat("en-US", { timeZone, dateStyle: "medium", timeStyle: "short" })).format(value)
  const zoneName = cachedFormatter(`zone:${timeZone}`, () =>
    new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" }))
    .formatToParts(value)
    .find(part => part.type === "timeZoneName")?.value
  return zoneName ? `${text} ${zoneName}` : text
}
